<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useSummaryReportArchive } from '../composables/useSummaryReportArchive';

const props = defineProps({
  matrixData: {
    type: Object,
    default: () => ({ teams: [], matrix: [] }),
  },
  archiveContext: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const { openTransferMatrixArchive } = useSummaryReportArchive();

const teams = computed(() => props.matrixData.teams || []);
const matrix = computed(() => props.matrixData.matrix || []);

const transfersOutByTeam = computed(
  () =>
    props.matrixData.transfers_out_by_team ||
    props.matrixData.transfersOutByTeam ||
    {}
);
const transfersInByTeam = computed(
  () =>
    props.matrixData.transfers_in_by_team ||
    props.matrixData.transfersInByTeam ||
    {}
);

const teamKey = team => (team?.id == null ? 'null' : String(team.id));

const lookupTotal = (map, team) => {
  if (!map || !team) return 0;
  if (team.id == null || team.id === '') {
    return Number(map.null ?? map.null ?? map[''] ?? 0);
  }
  return Number(map[team.id] ?? map[String(team.id)] ?? 0);
};

const rowTotal = team => lookupTotal(transfersOutByTeam.value, team);
const colTotal = team => lookupTotal(transfersInByTeam.value, team);

const grandTotal = computed(() =>
  teams.value.reduce((sum, team) => sum + rowTotal(team), 0)
);

const isLinkable = (fromTeam, toTeam, value) =>
  fromTeam?.id !== toTeam?.id &&
  value !== null &&
  Number(value) > 0 &&
  (fromTeam?.id != null || toTeam?.id != null);

const onCellClick = (fromTeam, toTeam, value) => {
  if (!isLinkable(fromTeam, toTeam, value)) return;

  openTransferMatrixArchive({
    fromTeam,
    toTeam,
    context: props.archiveContext,
  });
};
</script>

<template>
  <section
    class="px-6 py-5 shadow outline-1 outline outline-n-container rounded-xl bg-n-solid-2 overflow-auto"
  >
    <h3 class="text-base font-medium text-n-slate-12">
      {{ $t('SUMMARY_REPORTS.CHARTS.TRANSFER_MATRIX') }}
    </h3>
    <table v-if="teams.length" class="mt-4 min-w-full text-sm">
      <thead>
        <tr>
          <th class="p-2 text-left text-n-slate-11" />
          <th
            v-for="team in teams"
            :key="`col-${teamKey(team)}`"
            class="p-2 text-left text-n-slate-11"
          >
            {{ team.name }}
          </th>
          <th class="p-2 text-left text-n-slate-11 font-medium">
            {{ $t('SUMMARY_REPORTS.CHARTS.TRANSFERS_OUT') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(fromTeam, rowIndex) in teams"
          :key="`row-${teamKey(fromTeam)}`"
        >
          <th class="p-2 text-left font-medium text-n-slate-12">
            {{ fromTeam.name }}
          </th>
          <td
            v-for="(toTeam, colIndex) in teams"
            :key="`${teamKey(fromTeam)}-${teamKey(toTeam)}`"
            class="p-2 text-n-slate-12"
          >
            <button
              v-if="isLinkable(fromTeam, toTeam, matrix[rowIndex]?.[colIndex])"
              type="button"
              class="text-n-brand hover:underline cursor-pointer tabular-nums"
              :title="t('SUMMARY_REPORTS.ARCHIVE_LINK.TOOLTIP')"
              @click="
                onCellClick(fromTeam, toTeam, matrix[rowIndex]?.[colIndex])
              "
            >
              {{ matrix[rowIndex]?.[colIndex] }}
            </button>
            <span v-else>
              {{
                matrix[rowIndex]?.[colIndex] === null
                  ? '—'
                  : matrix[rowIndex]?.[colIndex]
              }}
            </span>
          </td>
          <td class="p-2 font-medium text-n-slate-12 tabular-nums">
            {{ rowTotal(fromTeam) }}
          </td>
        </tr>
        <tr>
          <th class="p-2 text-left font-medium text-n-slate-12">
            {{ $t('SUMMARY_REPORTS.CHARTS.TRANSFERS_IN') }}
          </th>
          <td
            v-for="team in teams"
            :key="`in-${teamKey(team)}`"
            class="p-2 font-medium text-n-slate-12 tabular-nums"
          >
            {{ colTotal(team) }}
          </td>
          <td class="p-2 font-medium text-n-slate-12 tabular-nums">
            {{ grandTotal }}
          </td>
        </tr>
      </tbody>
    </table>
    <p v-else class="mt-4 text-sm text-n-slate-11">
      {{ $t('SUMMARY_REPORTS.CHARTS.NO_DATA') }}
    </p>
  </section>
</template>
